import { useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Plus, Search } from 'lucide-react'
import { getLocations, getUsers } from '../../api/master'
import Spinner from '../../components/Spinner'
import ErrorBanner from '../../components/ErrorBanner'
import { FilterChips, StatusBadge } from '../../components/PageParts'
import { useSession } from '../../session'
import { MasterTabs, ROLE_LABEL, cashierOnly, roleList, userActive } from './masterShared'

const SHOW = [
  { id: 'active', label: 'Active', test: userActive },
  { id: 'inactive', label: 'Inactive', test: (u) => !userActive(u) },
  { id: 'all', label: 'All', test: () => true },
]

// Users: who can sign in, what they can open, and their home location.
export default function UserList() {
  const [params, setParams] = useSearchParams()
  const show = SHOW.some((s) => s.id === params.get('show')) ? params.get('show') : 'active'
  const [search, setSearch] = useState('')
  const { data: users = [], isLoading, error } = useQuery({ queryKey: ['users'], queryFn: getUsers })
  // The plan includes some users; cashiers are free.
  const { user } = useSession()
  const limit = user?.user_limit
  // A practice's accountants are free (Settings → Accountant).
  const counted = users.filter((u) => userActive(u) && !cashierOnly(u.role) && !u.practice_id).length
  const cashiers = users.filter((u) => userActive(u) && cashierOnly(u.role)).length
  const { data: locations = [] } = useQuery({ queryKey: ['locations'], queryFn: getLocations })
  const locName = (id) => locations.find((l) => Number(l.location_id) === Number(id))?.name
  const counts = useMemo(() => Object.fromEntries(SHOW.map((s) => [s.id, users.filter(s.test).length])), [users])
  const shown = useMemo(() => {
    const term = search.trim().toLowerCase()
    const test = SHOW.find((s) => s.id === show).test
    return users
      .filter((u) => test(u) && (!term || [u.full_name, u.username, u.email].join(' ').toLowerCase().includes(term)))
      .sort((a, b) => String(a.full_name || a.username).localeCompare(String(b.full_name || b.username)))
  }, [users, search, show])

  return (
    <div className="ci-page">
      <MasterTabs />
      <div className="ci-header-row">
        <div className="ci-title-block">
          <div>
            <h1>Users</h1>
            <div className="ci-title-meta">Who can sign in, and what each person can open.{limit != null && ` ${counted} of ${limit} users on your plan${cashiers ? `, plus ${cashiers} cashier${cashiers === 1 ? '' : 's'} (free)` : ''}.`}</div>
          </div>
        </div>
        <div className="ci-header-actions">
          <Link className="ci-btn ci-btn-primary" to="/master/users/new" title={limit != null && counted >= limit ? 'Your plan’s users are all used: add a cashier (free), or add a user under Settings → Plan & billing ($8/month).' : undefined}><Plus size={15} />New user</Link>
        </div>
      </div>

      <div className="ci-toolbar">
        <label className="ci-search" aria-label="Search users">
          <Search size={14} />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Name, username or email…" />
        </label>
      </div>
      <FilterChips options={SHOW.map((s) => ({ id: s.id, label: s.label, count: counts[s.id] }))} value={show}
        onChange={(id) => setParams((p) => { const n = new URLSearchParams(p); if (id === 'active') n.delete('show'); else n.set('show', id); return n }, { replace: true })} />

      {error && <ErrorBanner message="Couldn’t load the users." />}

      <div className="ci-table-wrap">
        <div className="ci-table-scroll">
          <table className="ci-table fit-sm">
            <thead><tr><th>User</th><th>Can open</th><th className="hide-sm">Home location</th><th className="center hide-sm">Status</th></tr></thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan="4"><Spinner label="Loading users…" /></td></tr>
              ) : shown.length === 0 ? (
                <tr><td colSpan="4"><div className="ci-empty">No users here.</div></td></tr>
              ) : shown.map((u) => (
                <tr key={u.user_id}>
                  {u.practice_id ? (
                    <>
                      <td>
                        <Link className="ci-strong mf-row-link" to="/settings/accountant">{u.full_name || u.username}</Link>
                        <span className="cell-sub">Your accountant, from {u.practice_name}</span>
                      </td>
                      <td><span className="mf-roles"><span className="mf-role">Accountant</span><span className="mf-role">{u.practice_name}</span></span></td>
                    </>
                  ) : (
                    <>
                      <td>
                        <Link className="ci-strong mf-row-link" to={`/master/users/${u.user_id}/edit`}>{u.full_name || u.username}</Link>
                        <span className="cell-sub">@{u.username}{u.email ? ` · ${u.email}` : ''}</span>
                      </td>
                      <td><span className="mf-roles">{roleList(u.role).map((r) => <span key={r} className="mf-role">{ROLE_LABEL[r] || r}</span>)}</span></td>
                    </>
                  )}
                  <td className="hide-sm ci-muted-cell">{locName(u.location_id) || (roleList(u.role).includes('admin') ? 'All locations' : '–')}</td>
                  <td className="center hide-sm">{userActive(u) ? <StatusBadge label="Active" tone="good" /> : <StatusBadge label="Inactive" tone="muted" />}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="ci-pagination"><span className="ci-page-info">{shown.length} {shown.length === 1 ? 'user' : 'users'}</span></div>
      </div>
    </div>
  )
}
